import React from "react";
import { QuestionCircleOutlined } from "@ant-design/icons";

const tutorialSteps = [
  {
    id: 0,
    title: "CRP Basics",
    text: (
      <>
        To get started, click <b>CRP Basics</b> in the navigation bar.
      </>
    ),
    target: ".crp-basics",
    route: "/",
    advanceType: "click",
    canScroll: false,
  },
  {
    id: 1,
    title: "CRP Basics Page",
    text: (
      <>
        Here you will find basic information about CRP and answers to frequently
        asked questions.
        <br />
        <br />
        Click <b>Next</b> to continue.
      </>
    ),
    target: ".crp-basics1",
    route: "/crpbasics",
    advanceType: "next",
    canScroll: true,
  },
  {
    id: 2,
    title: "Explore Practices",
    text: (
      <>
        To view CRP practices available in your area, click{" "}
        <b>Explore Practices</b>.
      </>
    ),
    target: ".explore-practices",
    route: "/crpbasics",
    advanceType: "click",
    canScroll: false,
  },
  {
    id: 3,
    title: "State Selection",
    text: (
      <>
        Use the <b>State</b> dropdown to view currently available states.
        <br />
        <br />
        <i>Select one to continue.</i>
      </>
    ),
    target: ".state-sel-tutorial",
    route: "/PracticeList",
    advanceType: "custom",
    canScroll: false,
    wizardStep: 0,
  },
  {
    id: 4,
    title: "County Selection",
    text: (
      <>
        Once a state is selected, use the <b>County</b> dropdown to choose your
        county.
        <br />
        <br />
        <i>Make a selection to continue.</i>
      </>
    ),
    target: ".county-sel-tutorial",
    route: "/PracticeList",
    advanceType: "custom",
    canScroll: false,
    wizardStep: 0,
  },
  {
    id: 5,
    title: "Continue",
    text: (
      <>
        Wait for the map to finish zooming and highlight your county.
        <br />
        <br />
        Then click the <b>Next</b> button to continue.
      </>
    ),
    target: ".location-sel-next",
    route: "/PracticeList",
    advanceType: "click",
    canScroll: false,
    wizardStep: 0,
  },
  {
    id: 6,
    title: "Land Cover Type",
    text: (
      <>
        Click to select your <b>land cover type</b>. Hover over the
        <QuestionCircleOutlined style={{ marginLeft: 4 }} /> icon to see a
        description of each option.
        <br />
        <br />
        We’ll just pick one for now — but you can change or add more later.
      </>
    ),
    target: ".land-cover-tutorial",
    route: "/PracticeList",
    advanceType: "custom",
    canScroll: false,
    wizardStep: 1,
  },
  {
    id: 7,
    title: "Continue",
    text: (
      <>
        Click the <b>Next</b> button to continue.
      </>
    ),
    target: ".location-sel-next",
    route: "/PracticeList",
    advanceType: "custom",
    canScroll: false,
    wizardStep: 1,
  },
  {
    id: 8,
    title: "Select Priorities",
    text: (
      <>
        Click to select a <b>priority</b> you’d like to address on your field.
        <br />
        <br />
        Hover over <QuestionCircleOutlined style={{ marginLeft: 4 }} /> to get a
        description of each priority.
        <br />
        <br />
        Select <b>three</b> for now. You can add or remove them later.
      </>
    ),
    target: ".priority-sel-tutorial",
    route: "/PracticeList",
    advanceType: "custom",
    canScroll: false,
    wizardStep: 2,
  },
  {
    id: 9,
    title: "Continue",
    text: (
      <>
        Click the <b>Next</b> button to continue.
      </>
    ),
    target: ".location-sel-next",
    route: "/PracticeList",
    advanceType: "custom",
    canScroll: false,
    wizardStep: 2,
  },
  {
    id: 10,
    title: "Rank Priorities",
    text: (
      <>
        Click and drag an icon to <b>reorder</b> your selected priorities.
        <br />
        <br />
        <i>Move one for now. You can change the order again later.</i>
      </>
    ),
    target: ".ranking-tutorial",
    route: "/PracticeList",
    advanceType: "custom",
    canScroll: false,
    wizardStep: 3,
  },
  {
    id: 11,
    title: "Confirm",
    text: (
      <>
        Click the <b>Confirm</b> button to continue.
      </>
    ),
    target: ".confirm-tutorial",
    route: "/PracticeList",
    advanceType: "custom",
    canScroll: false,
    wizardStep: 3,
  },
  {
    id: 12,
    title: "Practice Card",
    text: (
      <>
        This is a <b>Practice Card</b>.
        <br />
        <br />
        It provides a quick summary of the practice and helps you compare
        options at a glance.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    target: ".practice-card-tutorial",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 13,
    title: "Practice Number",
    text: (
      <>
        This is the <b>practice number</b>, often used when referring to
        practices.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    target: ".tutorial-practice-number",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 14,
    title: "Practice Title",
    text: (
      <>
        This is the <b>full name</b> of the practice.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    target: ".tutorial-practice-title",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 15,
    title: "Land Cover",
    text: (
      <>
        This shows the <b>land cover types</b> that can be enrolled in this
        practice.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    target: ".tutorial-practice-cover",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 16,
    title: "Priorities",
    text: (
      <>
        These are the <b>conservation priorities</b> this practice helps
        address.
        <br />
        <br />
        <i>Hover over the icons to get a reminder of what each represents.</i>
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    target: ".tutorial-practice-priorities",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 17,
    title: "Signup Types",
    text: (
      <>
        These are the <b>signup types</b> available for this practice.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    target: ".tutorial-practice-signup",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 18,
    title: "Additional Incentives",
    text: (
      <>
        These are the <b>incentives</b> that may be available for this practice.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    target: ".tutorial-practice-incentives",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 19,
    title: "Full Details",
    text: (
      <>
        You can click <b>Details</b> to open a new tab with a more comprehensive
        overview of the practice, including its requirements.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    target: ".tutorial-practice-details",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },
  {
    id: 20,
    title: "Priority Match",
    text: (
      <>
        The <b>priority match</b> shows how well this practice aligns with your
        selections.
        <br />
        <i>Rated from Best to Fair.</i>
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    target: ".tutorial-practice-match",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 21,
    title: "Most Common Flag",
    text: (
      <>
        This practice is marked with the green <b>Most Common</b> flag.
        <br />
        <br />
        These show practices which are frequently enrolled by landowners in your
        selected area.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    target: ".tutorial-most-common-card",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
    condition: "hasMostCommon",
  },

  {
    id: 22,
    title: "CREP Practices",
    text: (
      <>
        This practice has additional options under the <b>CREP</b> program.
        <br />
        <br />
        Click the card to flip and view the available CREP options.
      </>
    ),
    target: ".tutorial-crep-card",
    route: "/PracticeList",
    advanceType: "custom",
    canScroll: false,
    condition: "hasCrep",
  },

  {
    id: 23,
    title: "Practice Details & CREP Options",
    text: (
      <>
        Here you'll find links to the <b>CREP details</b> page and the general
        practice details.
        <br />
        <br />
        Clicking one of these links will open a new page with more information.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    target: ".tutorial-crep-back-content",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
    condition: "hasCrep",
  },
  {
    id: 24,
    title: "Viewing Options",
    text: (
      <>
        You can use this dropdown to switch between <b>Grid View</b> and{" "}
        <b>List View</b>.
        <br />
        <br />
        It also includes an option to <b>print</b> your filtered practices.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    target: ".list-view-tutorial",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 25,
    title: "Change Selections",
    text: (
      <>
        You can click the <b>Filters</b> button to reopen the filter panel.
        <br />
        <br />
        From there you can update your selected priorities or adjust their
        rankings here.
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    target: ".filter-button-tutorial",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 26,
    title: "Reset All",
    text: (
      <>
        You can click <b>Reset</b> to start your search over at any time.
        <br />
        <br />
        <i>This will remove all your previous selections and filters.</i>
        <br />
        <br />
        <b>Click Next to continue.</b>
      </>
    ),
    target: ".reset-button-tutorial",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },

  {
    id: 27,
    title: "You're All Set!",
    text: (
      <>
        You've completed the tutorial — great job!
        <br />
        <br />
        There's even more coming soon, including a walkthrough of the{" "}
        <b>Check Eligibility</b> section.
        <br />
        <br />
        Feel free to explore on your own — you can always restart the tutorial
        by navigating back to the homepage.
      </>
    ),
    target: "center",
    route: "/PracticeList",
    advanceType: "next",
    canScroll: false,
  },
];

export default tutorialSteps;
